/* Architect API client — every /api/architect endpoint the SPA calls.
   The global fetchInterceptor attaches the Bearer token to any fetch
   whose origin matches the API origin, so these plain fetches need no
   auth plumbing. Error mapping mirrors the vanilla api() helper:
   ERROR_EN → server message → server error → status, plus the first
   few validator errors[]. */

import { ERROR_EN } from './utils';
import { AuthedUser } from '../auth/api';
import { stageAccess } from './stageAccess';
import {
  ProjectSummary,
  ProjectDetail,
  ProjectRecord,
  FileMeta,
  RenderResult,
  Spec,
  ArchitectureEditRequest,
} from './types';

/* Stage permissions come from the server; older servers retain role defaults. */
export interface EstimateAccess {
  canViewArchitecture: boolean;
  canApproveArchitecture: boolean;
  canViewEstimate: boolean;
  canApproveEstimate: boolean;
  /** Architecture editing, including source inputs and generation. */
  canGenerate: boolean;
  /** Estimate editing; viewing and approval have separate capabilities. */
  canEstimate: boolean;
}

/** Server capabilities take priority over legacy role defaults. */
export function estimateAccess(
  user: AuthedUser | null,
  permissions: string[],
): EstimateAccess {
  const architecture = stageAccess(user, permissions, 'architecture');
  const estimate = stageAccess(user, permissions, 'estimate');
  return {
    canGenerate: architecture.edit,
    canEstimate: estimate.edit,
    canViewArchitecture: architecture.view,
    canApproveArchitecture: architecture.approve,
    canViewEstimate: estimate.view,
    canApproveEstimate: estimate.approve,
  };
}

/** The separate Pricing right does not grant estimate/proposal or admin access. */
export function pricingAccess(user: Pick<AuthedUser, 'isRoot' | 'stage_access'> | null, permissions: readonly string[]): boolean {
  return stageAccess(user, permissions, 'pricing').view;
}

/* ═══════════ RUN / INPUT TYPES ═══════════════════════════════
   Generation is a tracked async RUN (DESIGN5 §A): POST generate/prompt
   returns a run immediately (202) and the UI polls GET /runs/:id until
   it is done/error. These shapes mirror the backend RunRow / PhaseInput
   columns 1:1 (snake_case on the wire, no mapping layer). Kept local to
   the API client — types.ts owns the poster-spec types only. */

export type RunKind = 'generate' | 'prompt';
export type RunStatus = 'running' | 'done' | 'error';
/** Live sub-step of a run; drives the RunProgress stepper. */
export type RunStage =
  | 'preparing'
  | 'llm'
  | 'llm-retry'
  | 'validating'
  | 'rendering'
  | 'saving';

export interface Run {
  id: string;
  project_id: string;
  kind: RunKind;
  status: RunStatus;
  stage: string; // one of RunStage, but tolerate unknown values
  model: string | null;
  instruction: string | null;
  error: string | null;
  telemetry: unknown | null;
  version_id: string | null;
  created_at: string;
  updated_at: string;
  finished_at: string | null;
}

/** One requested phase in the phase plan (title optional). */
export interface Phase {
  title?: string | null;
  description: string;
}

/** Body of PUT /projects/:id/inputs — every key is an independent
    partial update. */
export interface ProjectInputs {
  main_transcript?: string;
  extra_notes?: string;
  phases?: Phase[];
}

/* ═══════════ PIPELINE (DESIGN11 §P1, DESIGN23 §C2) ═══════════
   The four-stage snapshot the Architect surface renders as status chips —
   since DESIGN23 in the order «Architecture · Estimate · Pricing · Proposal»,
   because a proposal is approved with its final numbers. Only the DISPLAY
   order moved; the fields below are unchanged and the backend derives them
   exactly as before (architect/store.derivePipeline). Returned on every
   projects-list row, on GET /projects/:id, and on the architecture-approve
   response. Statuses ONLY — never any content — so it is visible to every
   architect-surface role.
     architecture: 'none' (no spec) | 'draft' (spec, unapproved) | 'approved'
     estimate/proposal: 'none' | 'review' (any in review) | 'approved'
     priced: any proposal carries pricing (root-only prices exist) */
/* DESIGN28 — the price sheet belongs to the PROJECT, not to a proposal, so it
   can be filled in before any document exists. Root-only in every form. */
/* DESIGN29 — root's per-approver overview + recommended prices. */
export interface StageDetail {
  state: 'idle' | 'generating' | 'awaiting' | 'approved';
  given: number;
  required: number;
  voted: Array<{ name: string | null; at: string }>;
  waiting: string[];
  /** DESIGN46 — with an ordered list, the one person it is waiting for now. */
  turn?: string | null;
  approved_by?: string | null;
  approved_at?: string | null;
}
export interface PricingStageDetail { state: 'idle' | 'awaiting' | 'done'; by?: string | null; at?: string | null; assigned_to_name?: string | null }
export interface ProjectOverview {
  architecture: StageDetail;
  estimate: StageDetail;
  pricing: PricingStageDetail;
  proposal: StageDetail;
}
export interface ApprovalsOverview {
  policies: Array<{ step: string; required_count: number; users: Array<{ id: string; username: string; display_name: string | null }> }>;
  projects: Record<string, ProjectOverview>;
}
export interface CaseAdvice {
  case: string;
  days: number;
  price: string | null;
  monthly: string | null;
  formula: string;
  cost?: number | null;
  suggest?: { unit: UsageUnit; rate: number; minimum: number; why: string } | null;
}
export interface PricingMethodology {
  day_rate: number;
  monthly_pct: number;
  note: string | null;
  updated_by_name?: string | null;
  updated_at?: string | null;
  rules?: PricingRules;
}

/* DESIGN45 — the CEO's two-screen pricing. Wire shapes mirror
   backend/src/pricing/derive.ts 1:1; the arithmetic lives in pricingMath.ts
   and is the same on both surfaces and the server. */
export type EngineerLevel = 'middle' | 'senior';
export type MonthlyModel = 'fixed' | 'usage' | 'simple';
export type UsageUnit = 'call' | 'sms' | 'email' | 'sms_email' | 'custom';
export type PlanMode = 'sequential' | 'parallel' | 'custom';
export type PricingSection = 'development' | 'qa' | 'setup' | 'monthly';
export interface PricingSetupRow {
  case: string;
  /** Timeline for this case, in working weeks (≤ 2 decimals). */
  weeks: string;
  /** One-time setup fee for this case. */
  price: string | null;
  /** Legacy per-case subscription. */
  monthly?: string | null;
  days?: number | null;
  /** 1-based; rows sharing a stage run in parallel, stages run in order. */
  stage?: number;
  engineers?: number;
  level?: EngineerLevel;
  blurb?: string;
}
export type PricingCaseSection = 'development' | 'setup';
export interface PricingCasePlan {
  case: string;
  dev_weeks: number;
  dev_engineers: number;
  dev_level: EngineerLevel;
  dev_week_rate?: number;
  internal_cost?: number | null;
  sale_price?: number | null;
  section_confirmations?: Partial<Record<PricingCaseSection, string>>;
}
export interface PricingQACase {
  case: string;
  /** Null is an undecided draft; zero explicitly means no QA for this case. */
  weeks: number | null;
  /** QA stages run after development and are independent of development stages. */
  stage: number;
}
export interface PricingPlan {
  mode: PlanMode; qa_weeks: number; qa_engineers?: number; qa_level?: EngineerLevel;
  /** Omitted preserves the historical project-wide QA allowance. */
  qa_cases?: PricingQACase[];
  qa_mode?: PlanMode;
  workflow_version?: 'lato-pricing-v2';
  dev_weeks?: number; dev_engineers?: number; dev_level?: EngineerLevel;
  dev_week_rate?: number;
  internal_cost?: number | null; sale_price?: number | null;
  case_pricing?: PricingCasePlan[];
  section_confirmations?: Partial<Record<PricingSection, string>>;
}
export type MonthlyBucket = 'communication' | 'processing' | 'infrastructure' | 'third_party';
export type MonthlyRuleId =
  | 'sms' | 'email' | 'sms_email' | 'voice' | 'mailbox'
  | 'processing_heavy' | 'processing_light' | 'generated_document'
  | 'platform_500' | 'platform_750' | 'platform_850' | 'hosting_500' | 'hosting_750'
  | 'ap_unlimited' | 'secondary_unlimited' | 'payroll'
  | 'third_party' | 'third_party_record' | 'phase_addon' | 'phase_user';
export interface MonthlyEvidence { source: string; quote: string }
export interface MonthlyCharge {
  id: string;
  ruleId: MonthlyRuleId;
  label: string;
  unitLabel?: string;
  quantity?: number | null;
  minimum?: number | null;
  unitCost?: number | null;
  manualRate?: number | null;
  evidence: MonthlyEvidence;
  internalMonthlyCost?: number | null;
  activityIds?: string[];
  /** Undefined retains historical scope; [] explicitly covers the whole project. */
  caseNames?: string[];
}
export interface MonthlySimple {
  policyVersion: 'lato-monthly-v1';
  bucket: MonthlyBucket;
  charges: MonthlyCharge[];
}
export interface MonthlyActivity { id: string; summary: string; bucket: MonthlyBucket; evidence: MonthlyEvidence }
export interface MonthlyQuestion { key: string; label: string; unit: string; required: boolean; value?: number }
export interface MonthlyRecommendation {
  version: 'lato-monthly-v1';
  model?: string;
  sourceEstimateId?: string;
  sourceFingerprint: string;
  analysisToken: string;
  summary: string;
  activities: MonthlyActivity[];
  rows: PricingMonthlyRow[];
  exampleMonth: { formula: string; total: number | null };
  derivation: string[];
  benchmarks: Array<{ id: string; filename: string; client?: string; sha256?: string }>;
  questions: MonthlyQuestion[];
  warnings: string[];
  applyReady: boolean;
  preservedBaseline?: PricingMonthlyRow[];
}
export interface MonthlyBenchmarkDocument {
  id: string;
  filename: string;
  client: string;
  sha256: string;
  activityTags: string[];
  actualRecommendedSimpleStructure: Array<{ label: string; price: string }>;
  derivationText: string;
  flaggedConflictsWithCurrentRules?: Array<{ code: string; detail: string }>;
}
export interface MonthlyPricingLibrary {
  model: string;
  policyVersion: string;
  documents: MonthlyBenchmarkDocument[];
}
export interface MonthlyRecommendationRequest {
  model?: string;
  analysisToken?: string;
  answers?: Record<string, number>;
  current_sheet?: PricingSheet;
}
export interface PricingMonthlyRow {
  label: string;
  price: string | null;
  overage: string | null;
  description?: string;
  /** Explicit client-facing text for policy rows; legacy labels may contain internal notes. */
  displayLabel?: string;
  displayDescription?: string;
  model?: MonthlyModel;
  simple?: MonthlySimple;
  unit?: UsageUnit;
  rate?: number | null;
  unit_label?: string;
  minimum?: number | null;
  amount?: number | null;
  case?: string;
  /** One shared tariff for these cases, not one charge per selected case. */
  caseNames?: string[];
}
export interface PricingSheet { setup: PricingSetupRow[]; monthly: PricingMonthlyRow[]; plan?: PricingPlan }
/** Saved values only: this read never derives a sheet or advances a stage. */
export interface PricingHistoryResponse {
  read_only: true;
  saved: boolean;
  sheet: PricingSheet | null;
  estimate_id: string | null;
  status: 'draft' | 'review' | 'approved' | null;
  updated_at: string | null;
  approved_at: string | null;
}
export interface WorkflowRecoveryInput {
  proposal_id: string;
  architecture_version_id?: string;
  keep_current_wording?: boolean;
}
export interface WorkflowRecoveryPreview {
  project_id?: string;
  can_restore: boolean;
  blockers: string[];
  fingerprint: string;
  architecture_versions: Array<{ id: string; seq: number; created_at: string; source: string; phase_title?: string }>;
  selected_architecture_version_id: string | null;
  selection_suggested?: boolean;
  keep_current_wording: boolean;
  wording_compatible: boolean;
  architecture: { current_version_id: string | null; title: string | null; phase_title: string | null; case_titles: string[] } | null;
  source_estimate: { id: string; title: string; status: string; created_at: string; reviewed_at: string | null } | null;
  source_proposal: { id: string; status: string; created_at: string; updated_at: string | null; client: string; document_revision: number } | null;
  saved_pricing: Pick<PricingSheet, 'setup' | 'monthly'> | null;
}
export interface WorkflowRecoveryResult {
  restored: true;
  replayed: boolean;
  recovery_id: string;
  estimate_id: string;
  proposal_id: string;
  architecture_version_id: string;
  project_id: string;
}
export interface PricingRules {
  week_days: number;
  middle_week_rate: number;
  senior_week_rate: number;
  unit_rates: Record<Exclude<UsageUnit, 'custom'>, number>;
  unit_minimums: Record<UsageUnit, number>;
}
export interface PricingSourceRationale {
  source_estimate_id: string | null;
  working_days: number; source_weeks: number; selected_weeks: number; total_weeks: number;
  has_unestimated_work: boolean;
  summary: string; schedule_note: string;
  cases: Array<{
    name: string; working_days: number; weeks: number;
    modules: Array<{ name: string; days: number | [number, number] | null; description: string; tbd_reason?: string }>;
  }>;
}
export interface PricingSheetResponse {
  /** Server-validated review routing and current editor access. */
  can_submit?: boolean;
  approvals?: ApprovalStatus;
  sheet: PricingSheet;
  saved: boolean;
  priced: boolean;
  /** A saved sheet is a draft until the assigned owner approves it. */
  status?: 'draft' | 'review' | 'approved';
  estimate_id?: string | null;
  /** Approved source identity; local pricing decisions do not change it. */
  source_reviewed_at?: string | null;
  submitted_by?: string | null;
  submitted_by_name?: string | null;
  submitted_at?: string | null;
  review_revision?: number;
  rejection_reason?: string | null;
  rejected_by_name?: string | null;
  rejected_at?: string | null;
  approved_by_name?: string | null;
  approved_at?: string | null;
  assigned_to?: string | null;
  assigned_to_name?: string | null;
  assigned_to_username?: string | null;
  assigned_to_active?: boolean;
  assigned_at?: string | null;
  stale: boolean;
  updated_by_name: string | null;
  updated_at: string | null;
  has_proposal: boolean;
  has_project_sheet?: boolean;
  legacy_pricing?: boolean;
  /** Convenience for the panel: no rows means no approved estimate yet. */
  setup_empty?: boolean;
  /** Set on approval: a proposal was generated from these prices. */
  auto_started?: 'proposal' | null;
  /** Set on approval: a proposal already existed and now carries these prices. */
  repriced?: boolean;
  /** A native document needs manual review when its fee table cannot be updated safely. */
  reprice_error?: string;
  reprice_detail?: string;
  /** GET only — recommended prices per case, with the arithmetic. */
  advice?: CaseAdvice[];
  methodology?: PricingMethodology;
  source_rationale?: PricingSourceRationale;
}

/** The panel only needs to know «are there any case rows yet»; deriving it here
 *  keeps that question out of the component. */
function withSetupEmpty(r: any): PricingSheetResponse {
  return { ...r, setup_empty: !r?.sheet?.setup?.length };
}

export interface Pipeline {
  architecture: 'none' | 'draft' | 'approved';
  estimate: 'none' | 'review' | 'approved';
  pricing?: 'none' | 'draft' | 'approved';
  proposal: 'none' | 'review' | 'approved';
  priced: boolean;
  /** Historical documents may exist even when the current revision has none. */
  historical?: { estimate: boolean; pricing?: boolean; proposal: boolean };
  /** Authoritative server gates; optional while older pods are rolling out. */
  unlocked?: { architecture: boolean; estimate: boolean; pricing: boolean; proposal: boolean };
  /* DESIGN12 §F1 — per-module liveness: true while that module has a run
     still in status 'running' for the project. The backend adds it to the
     SAME pipeline object (list + detail); optional here so a response from
     an older/partial backend (before the flag lands) still typechecks and
     the UI reads it tolerantly (a run flag is treated as false when absent). */
  running?: {
    architecture?: boolean;
    estimate?: boolean;
    proposal?: boolean;
  };
}

/** Read the explicit historical flags when available. Older servers only
 *  expose the stage status, so retain that fallback during rollout. */
export function pipelineHasHistoricalDocument(
  pipeline: Pipeline | null | undefined,
  stage: 'estimate' | 'pricing' | 'proposal',
): boolean {
  if (!pipeline) return false;
  if (stage === 'pricing') return pipeline.historical?.pricing ?? (pipeline.pricing != null && pipeline.pricing !== 'none');
  return pipeline.historical?.[stage] ?? pipeline[stage] !== 'none';
}

/* ═══════════ DOCUMENT CODES (DESIGN20) ═══════════════════════
   Every project carries an auto-assigned {YY}{SSS} number (e.g. 26078) and a
   doc_kind flag. The human-readable document CODES are COMPUTED from those two
   fields, never stored: W{number} for an architecture-workflow project,
   TS{number} when the engagement has a manual tech-scope doc instead, and
   LP{number} for the proposal (same number — the owner's own convention). Both
   fields are optional on the wire so a response from a backend that predates
   the migration still typechecks (an un-numbered project renders «No code»). */
export type DocKind = 'workflow' | 'tech_scope';

/** A projects-list row. Extends the poster-spec ProjectSummary with the
    pipeline snapshot + explicit architecture-approval fields the backend
    now returns. Every added field is optional so an older/partial response
    still typechecks (the UI reads them tolerantly). */
export interface ProjectListItem extends ProjectSummary {
  /** Optional client relationship. Missing/null preserves legacy projects. */
  client_id?: string | null;
  client_name?: string | null;
  client_code?: string | null;
  phase_title?: string | null;
  phase_order?: number | null;
  case_titles?: string[] | null;
  has_active_run?: boolean;
  architecture_status?: 'draft' | 'approved';
  architecture_approved_by_name?: string | null;
  architecture_approved_at?: string | null;
  pipeline?: Pipeline;
  /** DESIGN20 — the {YY}{SSS} project number (null / absent = no code yet). */
  number?: number | null;
  /** DESIGN20 — chooses the W… vs TS… document-code prefix. */
  doc_kind?: DocKind;
}

export interface ArchitectClient {
  id: string;
  code: string;
  name: string;
  domain: string | null;
  created_at: string;
  updated_at: string;
}

export interface ProjectClientLink {
  client_id: string | null;
  expected_client_id: string | null;
  phase_title?: string;
}

/** A single project record carrying the architecture-approval + pipeline
    fields (GET /projects/:id → project, and the approve response). Built on
    ProjectRecord's open index signature so tolerant reads compile. */
export type ArchitectProject = ProjectRecord & {
  architecture_status?: 'draft' | 'approved';
  architecture_approved_by_name?: string | null;
  architecture_approved_at?: string | null;
  pipeline?: Pipeline;
  /** DESIGN20 — see ProjectListItem. */
  number?: number | null;
  doc_kind?: DocKind;
};

/** Computed document codes for a project (DESIGN20). Never stored — this is
    the ONE place the rule lives; the mini app (§F2) imports the same helper so
    both surfaces render byte-identical codes. */
export interface ProjectCodes {
  /** `W26078` (workflow) | `TS26078` (tech scope) | null when un-numbered. */
  docCode: string | null;
  /** `LP26078` — ONLY when the project actually has a proposal document
      (pipeline.proposal !== 'none'); null otherwise. */
  proposalCode: string | null;
}

/** Derive the document codes from a project's number + doc_kind + pipeline.
    Un-numbered → both null. The proposal code appears only once a proposal
    document exists (any non-'none' proposal stage), matching the owner's rule
    that the workflow (W…/TS…) and the proposal (LP…) share the one number. */
export function projectCodes(
  number: number | null | undefined,
  docKind: DocKind | null | undefined,
  pipeline?: Pipeline | null,
): ProjectCodes {
  if (number == null) return { docCode: null, proposalCode: null };
  const docCode = (docKind === 'tech_scope' ? 'TS' : 'W') + number;
  const proposalCode =
    pipeline && pipeline.proposal !== 'none' ? 'LP' + number : null;
  return { docCode, proposalCode };
}

/* Architect-surface error codes not in the shared ERROR_EN table — the
   architecture-approval + proposal-gate flows (DESIGN11). Clean English,
   architect-surface tone (house style). Passed as a per-call errorMap.
   Exported (DESIGN15 §F2.2) as the single source the mini app also consumes,
   so the same code→English text is used on both surfaces — no fork. */
export const ARCHITECT_ERROR_EN: Record<string, string> = {
  architecture_edit_mode_required: 'Choose wording or structural edits before saving this approved architecture.',
  invalid_edit_mode: 'Choose wording or structural edits.',
  architecture_structure_changed: 'This changes the structure. Switch to Structural edits to save. Your draft is still here.',
  stale_architecture_version: 'The architecture changed since you opened it. Your draft is still here. Reload before saving.',
  architecture_wording_requires_approved: 'The architecture approval changed. Reload before saving your wording edits.',
  expected_version_id_required: 'Reload the architecture before saving. Your draft is still here.',
  expected_spec_fingerprint_required: 'Reload the architecture before saving. Your draft is still here.',
  approval_delegate_root_only: 'Only Root can approve for another reviewer.',
  invalid_approval_delegate: 'Choose an active reviewer for this stage.',
  pricing_approver_required: 'Choose a Pricing approver in Approvers.',
  pricing_assigned_elsewhere: 'Pricing is waiting for its assigned approver.',
  pricing_override_root_only: 'Only Root can override a Pricing assignment.',
  ineligible_pricing_assignee: 'Choose someone with Pricing access.',
  pricing_already_approved: 'Pricing is already approved.',
  pricing_revision_required: 'Refresh pricing before making a review decision.',
  pricing_review_changed: 'Pricing changed. Refresh and review the latest quote.',
  pricing_not_submitted: 'Send pricing for review before approval.',
  pricing_reason_required: 'Enter a reason for the requested changes.',
  invalid_review_revision: 'Refresh pricing and try again.',

  stale_recommendation: 'The project scope or pricing changed. Analyze again to use the latest sources.',
  recommendation_expired: 'This recommendation expired. Analyze again before applying.',
  recommendation_busy: 'A monthly analysis is already running. Please wait and try again.',
  no_approved_estimate: 'Approve an estimate before preparing the monthly structure.',
  monthly_recommendation_failed: 'The monthly scope could not be verified. Your prices are unchanged. Try analyzing again.',
  no_architecture: 'Generate the architecture first',
  no_approved_architecture: 'Approve the architecture first',
  already_approved: 'Architecture is already approved',
  // DESIGN23 §C1 — a configured approver list can refuse this caller.
  not_an_approver: 'You are not an approver for this step',
};

/* ═══════════ APPROVALS (DESIGN23 §C1) ═══════════════════════
   Per-step approver policies. THE RULE THAT OUTRANKS EVERYTHING HERE: with
   no policy configured the surface must look and behave EXACTLY as it does
   today — one approval, by anyone who already holds the right. Every type
   below is therefore read TOLERANTLY: a missing/failed status simply means
   «no policy», never a hidden button.

   Wire shapes mirror the backend module 1:1 (snake_case, no mapping layer):
     GET  /api/approvals/policies                → who approves each step
     PUT  /api/approvals/policies/:step          → root only
     GET  /api/approvals/status?…                → progress for one target */

export type ApprovalStep = 'architecture' | 'estimate' | 'proposal';
export type ApprovalTargetKind = 'project' | 'estimate' | 'proposal';

/** Approval policy stages, including Pricing's revision-bound review. */
export const APPROVAL_STEPS: ApprovalStep[] = ['architecture', 'estimate', 'proposal'];
export type ApprovalPolicyStep = ApprovalStep | 'pricing';
export const APPROVAL_POLICY_STEPS: ApprovalPolicyStep[] = ['architecture', 'estimate', 'pricing', 'proposal'];

/** One recorded vote. `user_name` is null when the viewer may not see WHO
    approved (the estimate step keeps its existing root-only identity rule);
    the TIME is visible to everyone, on every step. */
export interface ApprovalRow {
  user_id: string;
  user_name: string | null;
  approved_at: string;
  actor_user_id?: string | null;
  actor_name?: string | null;
}

/** A user as the approver picker / policy list shows them. */
export interface ApproverOption {
  id: string;
  username: string;
  display_name: string | null;
}

export interface StagePolicyAccess {
  /** null follows existing role permissions; [] explicitly grants no one. */
  viewer_ids: string[] | null;
  editor_ids: string[] | null;
}

export interface ApprovalPolicyWrite {
  required_count: number;
  user_ids: string[];
  ordered?: boolean;
  general_reviewer_id?: string | null;
  /** Omitted fields retain the saved setting. */
  access?: StagePolicyAccess | null;
  notification_user_ids?: string[] | null;
}

/** One stage's effective policy. An empty active list alone does not imply defaults. */
export interface ApprovalPolicyPublic {
  step: ApprovalPolicyStep;
  required_count: number;
  /** DESIGN46 «await» — the named people vote one after another, in list
      order; the next one is queued and pinged only after the previous one
      approved. Absent on an older backend = false. */
  ordered?: boolean;
  /** Active reviewers, retaining their relative configured order. */
  users: ApproverOption[];
  /** Root configuration keeps unavailable reviewers; users is the active list. */
  configured_user_ids?: string[];
  configured_ordered?: boolean;
  configured_required_count?: number;
  named_approvers_unavailable?: boolean;
  /** Author submission followed by this stage's final reviewer. */
  general_reviewer_id?: string | null;
  general_reviewer?: ApproverOption | null;
  access?: StagePolicyAccess | null;
  /** Stage-ready notifications, independent of approval-turn reminders. */
  notification_user_ids?: string[] | null;
  me?: { view: boolean; edit: boolean; approve: boolean; configured: boolean; edit_client_document?: boolean };
}

/** GET /policies. `eligible` is root-only (it feeds the picker). */
export interface ApprovalPolicies {
  policies: ApprovalPolicyPublic[];
  eligible?: Partial<Record<ApprovalPolicyStep, ApproverOption[]>>;
  /** Root-only memberships resolved by the server's authorization rules. */
  access_defaults?: Partial<Record<ApprovalPolicyStep, StageAccessDefaults>>;
  root_user_ids?: string[];
}

export interface StageAccessDefaults {
  viewer_ids: string[];
  editor_ids: string[];
  approver_ids: string[];
}

/** GET /mine — one card of the caller's «needs your approval» queue
    (DESIGN40). Eligibility is decided server-side with the approve
    endpoint's own rule, so tapping a card never ends in a 403. */
export interface ApprovalQueueItem {
  project_id: string;
  project_name: string | null;
  step: ApprovalStep | 'pricing';
  target_kind: ApprovalTargetKind;
  target_id: string;
  given: number;
  required: number;
  waiting: string[];
  /** DESIGN46 — the list is ordered and this card is the viewer's turn. */
  my_turn?: boolean;
}

/** GET /status — the progress source for every approve control. */
export interface ApprovalStatus {
  root_only?: boolean;
  step: ApprovalPolicyStep;
  stage_review?: {
    author: ApproverOption | null;
    general_reviewer: ApproverOption | null;
    phase: 'author' | 'general' | 'approved' | 'unavailable';
    version_id: string | null;
  };
  target_kind: ApprovalTargetKind;
  target_id: string;
  required: number;
  given: number;
  approvals: ApprovalRow[];
  /** The configured approvers (empty when no policy is set) — so the UI can
      say whom to ping. */
  approvers: ApproverOption[];
  /** DESIGN46 — the approvers go in order. */
  ordered?: boolean;
  /** DESIGN46 — with an ordered list, whose turn it is right now. */
  turn?: { id: string; name: string } | null;
  me: { can_approve: boolean; has_approved: boolean };
  architecture_review?: {
    author: ApproverOption | null;
    general_reviewer: ApproverOption | null;
    phase: 'author' | 'general' | 'approved' | 'unavailable';
    version_id: string | null;
  };
}

/** The `approvals` block the three approve responses now carry, so a caller
    can render «1 of 2» straight from the POST. Optional everywhere — an
    older/partial backend response still typechecks. */
export interface ApprovalProgress {
  given: number;
  required: number;
  mine?: boolean;
  approvals?: ApprovalRow[];
}

/** Approvals + pricing-gate error codes → English copy (house tone). */
export const APPROVALS_ERROR_EN: Record<string, string> = {
  stale_architecture_version: 'The architecture has changed. Reload it before approving.',
  invalid_general_reviewer: 'Choose an active General reviewer with Architect access.',
  architecture_review_unavailable: 'The author or General reviewer is unavailable. Ask Root to check the review settings.',
  architecture_reviewer_unavailable: 'The author or General reviewer is unavailable. Ask Root to check the review settings.',
  approval_delegate_root_only: 'Only Root can approve for another reviewer.',
  invalid_approval_delegate: 'Choose an active reviewer for this stage.',
  not_an_approver: 'You are not an approver for this step',
  not_your_turn: 'The approvers go in order — it is not your turn yet',
  pricing_required:
    'Set the prices first — the proposal is approved with its final numbers.',
  ineligible_approver:
    'Every approver must hold the right for that step — pick from the list',
  required_exceeds_approvers:
    'You need at least as many approvers as required approvals',
  invalid_pricing_policy: 'Check the Pricing approval settings',
  invalid_stage_access: 'Choose viewers, editors and notification recipients from active Architect accounts.',
  invalid_notification_user_ids: 'Choose notification recipients from the active Architect accounts.',
  invalid_required_count: 'Required approvals must be between 1 and 5',
  too_many_approvers: 'Too many approvers for one step',
  invalid_step: 'Unknown approval step',
  invalid_user_ids: 'Pick the approvers from the list',
  invalid_target_kind: 'Unknown approval target',
  invalid_target_id: 'Unknown approval target',
};

/** Post-map an ALREADY-thrown error whose message may still be a raw server
    code. The proposal client owns its own error table (a different module we
    do not edit), so a proposal approve can surface `not_an_approver` verbatim;
    this turns it into the same English copy the rest of the surface uses.
    Anything that isn't a known code passes through untouched. */
export function approvalErrorText(err: unknown): string {
  const msg =
    (err instanceof Error ? err.message : String(err ?? '')) ||
    'The request could not be completed';
  return APPROVALS_ERROR_EN[msg] || msg;
}

export const approvalsApi = {
  // Who is expected to approve each step. Readable by any architect:use
  // holder; root additionally gets `eligible` (the picker's source).
  getPolicies: (): Promise<ApprovalPolicies> =>
    reqUrl(APPROVALS_BASE + '/policies', { errorMap: APPROVALS_ERROR_EN }),

  // Replace one policy — Root only. Approved documents keep their decisions.
  putPolicy: (
    step: ApprovalPolicyStep,
    body: ApprovalPolicyWrite,
  ): Promise<{ policy: ApprovalPolicyPublic }> =>
    reqUrl(APPROVALS_BASE + '/policies/' + encodeURIComponent(step), {
      method: 'PUT',
      json: body,
      errorMap: APPROVALS_ERROR_EN,
    }),

  // The caller's personal queue — every awaiting step they may vote on now
  // and have not voted on. Any signed-in architect user; empty when nothing
  // waits for them.
  getMine: (): Promise<{ items: ApprovalQueueItem[] }> =>
    reqUrl(APPROVALS_BASE + '/mine', { errorMap: APPROVALS_ERROR_EN }),
  // Progress for one (target, step) + what THIS caller may do about it.
  getStatus: (q: {
    step: ApprovalStep;
    target_kind: ApprovalTargetKind;
    target_id: string;
  }): Promise<ApprovalStatus> =>
    reqUrl(
      APPROVALS_BASE +
        '/status?' +
        new URLSearchParams({
          step: q.step,
          target_kind: q.target_kind,
          target_id: q.target_id,
        }).toString(),
      { errorMap: APPROVALS_ERROR_EN },
    ),
};

/* PATCH /projects/:id error codes. Originally the rename surface (DESIGN18
   §F1.1): the name is validated exactly like project creation (invalid_name)
   and refused while the cascade is already running (rename_in_progress) or
   while any module run is live for the project (project_busy — reuses the
   pipeline running lookups). DESIGN20 threads number + doc_kind through the
   SAME PATCH, so their codes live here too: code_taken (the number is already
   another project's — 409) and invalid_doc_kind. Exported so the mini app
   (§F2) consumes the SAME code→English text — no fork across the two surfaces. */
export const RENAME_ERROR_EN: Record<string, string> = {
  invalid_name: 'Invalid project name',
  rename_in_progress: 'Rename already running — a moment',
  project_busy: 'Finish or stop the running generation first',
  // DESIGN20 — number / doc_kind edits share this endpoint.
  code_taken: 'This number is already used by another project.',
  invalid_number: 'Enter a number between 1000 and 99999',
  invalid_doc_kind: 'Invalid document type',
};

/** Response of PATCH /projects/:id (DESIGN18). Same {project, pipeline} shape
    as the other project endpoints (statuses only — no document data). The
    cascade re-renders the poster SVG + every estimate/proposal doc in place;
    if a single document fails to re-render the rename still lands and the
    response carries `warnings` (white-label English) for the ones that
    didn't update. Fields past `project` are optional so an older/partial
    backend still typechecks. */
export interface RenameResult {
  project: ArchitectProject;
  pipeline?: Pipeline;
  ok?: boolean;
  warnings?: string[];
}

const API = (process.env.REACT_APP_API_URL || 'http://localhost:3001').replace(
  /\/+$/,
  '',
);
const BASE = API + '/api/architect';
/* The approvals module is a SIBLING of /api/architect (DESIGN23 §A3) — it is
   mounted at the API root so a confined generator/estimator can read it. */
const APPROVALS_BASE = API + '/api/approvals';

export interface ApiError extends Error {
  status?: number;
  code?: string;
  body?: { error?: string; client?: ArchitectClient; [key: string]: unknown };
}

interface ReqOpts {
  method?: string;
  json?: unknown;
  body?: BodyInit;
  raw?: boolean;
  // Optional per-call code→text overrides checked before the shared
  // ERROR_EN table (used by the estimate endpoints, whose codes live
  // outside utils.ts).
  errorMap?: Record<string, string>;
}

async function mapError(
  res: Response,
  errorMap?: Record<string, string>,
): Promise<ApiError> {
  let msg = 'Error ' + res.status;
  let body: ApiError['body'];
  try {
    const j: any = await res.json();
    if (j && typeof j === 'object' && !Array.isArray(j)) body = j;
    // Prefer the call-specific text, then the mapped text; otherwise
    // surface the server "message" field, then the raw error code.
    msg =
      (errorMap && errorMap[j.error]) || ERROR_EN[j.error] || j.message || j.error || msg;
    // The validator returns error paths in errors[] — show the first
    // few so it's clear WHAT to fix, not just "invalid".
    if (Array.isArray(j.errors) && j.errors.length) {
      msg +=
        ': ' +
        j.errors.slice(0, 3).join('; ') +
        (j.errors.length > 3 ? ` (+${j.errors.length - 3})` : '');
    }
  } catch (_) {
    /* not json */
  }
  const err = new Error(msg) as ApiError;
  err.status = res.status;
  err.code = typeof body?.error === 'string' ? body.error : undefined;
  err.body = body;
  return err;
}

/* The one fetch. `reqUrl` takes a FULL url so a sibling API root (the
   approvals module lives at /api/approvals, not under /api/architect) can
   reuse the identical error mapping; `req` is the /api/architect shorthand
   every existing caller already uses. */
async function reqUrl(url: string, opts: ReqOpts = {}): Promise<any> {
  const headers: Record<string, string> = {};
  const init: RequestInit = { method: opts.method || 'GET', headers };
  if (opts.json !== undefined) {
    headers['Content-Type'] = 'application/json';
    init.body = JSON.stringify(opts.json);
  } else if (opts.body !== undefined) {
    init.body = opts.body; // FormData — the browser sets the boundary itself
  }
  let res: Response;
  try {
    res = await fetch(url, init);
  } catch (e) {
    throw new Error('Network unavailable — check your connection');
  }
  if (!res.ok) throw await mapError(res, opts.errorMap);
  if (opts.raw) return res;
  const ct = res.headers.get('content-type') || '';
  return ct.includes('application/json') ? res.json() : res.text();
}

function req(path: string, opts: ReqOpts = {}): Promise<any> {
  return reqUrl(BASE + path, opts);
}

/* ═══════════ STAGE COMMENTS (DESIGN43) ═══════════════════
   One thread per (project, pipeline stage), with @mentions. Who may open a
   stage's thread is the right that lets them act on it (root everywhere,
   generators on architecture/proposal, estimators on the estimate, pricing
   root-only); who may be mentioned is the server's eligible list for the
   stage — the client never widens either. */
export type CommentStage = 'architecture' | 'estimate' | 'pricing' | 'proposal';
export const COMMENT_STAGE_LABEL: Record<CommentStage, string> = {
  architecture: 'Architecture', estimate: 'Estimate', pricing: 'Pricing', proposal: 'Proposal',
};
export interface CommentPerson { id: string; username: string; display_name: string | null; }
export interface CommentAnchor {
  kind: 'block' | 'point'; document_id: string; revision?: string; path?: string;
  label: string; quote?: string; x?: number; y?: number;
}
export interface CommentAttachment {
  id: string; name: string; mime: string; size: number;
  kind: 'image' | 'video' | 'audio' | 'file'; url: string; duration_ms?: number;
}
export interface StageComment {
  id: string; stage: CommentStage; body: string;
  author: { id: string | null; name: string | null };
  mentions: CommentPerson[]; created_at: string; mine: boolean; can_delete: boolean;
  parent_id?: string | null; root_id?: string; anchor?: CommentAnchor | null;
  attachments?: CommentAttachment[]; edited_at?: string | null;
  resolved_at?: string | null; resolved_by?: unknown; deleted?: boolean;
  can_edit?: boolean; can_resolve?: boolean;
}
export interface CommentThread {
  stage: CommentStage; comments: StageComment[]; mentionable: CommentPerson[];
  can_write: boolean; next_cursor?: string | null; revision?: string | number;
}
export interface CommentPostOptions {
  parent_id?: string; anchor?: CommentAnchor; attachment_ids?: string[];
}
const COMMENTS_BASE = API + '/api/comments';
const COMMENT_ERRORS: Record<string, string> = {
  ...ARCHITECT_ERROR_EN,
  file_too_large: 'This file is too large. Images: 20 MB, videos: 50 MB, other files: 25 MB.',
  too_many_attachments: 'You can attach up to 10 files to one message.',
  unsupported_file_type: 'This file type is not supported. Try an image, video, audio clip, or document.',
  invalid_anchor: 'The selected document location is no longer available. Pick a new location.',
  empty_comment: 'Write a message or attach a file before sending.',
  empty_body: 'Write a message or attach a file before sending.',
  unsupported_media: 'This file type is not supported. Attach an image, video, audio clip, or document.',
  invalid_voice_recording: 'This voice clip could not be read. Record it again or attach an audio file.',
  invalid_attachments: 'One of the attachments is unavailable. Remove it and attach the file again.',
  upload_quota_exceeded: 'This project has reached its attachment limit. Remove unused uploads or contact an administrator.',
  uploads_busy: 'Another upload is still finishing. Please try again shortly.',
  thread_resolved: 'This thread is resolved. Reopen it before adding a reply.',
  thread_deleted: 'This thread was deleted. Start a new comment instead.',
};
const commentPath = (projectId: string, stage: CommentStage) => COMMENTS_BASE + '/' + encodeURIComponent(projectId) + '/' + stage;
export const commentsApi = {
  counts: (projectId: string): Promise<{ counts: Partial<Record<CommentStage, number>> }> =>
    reqUrl(COMMENTS_BASE + '/' + encodeURIComponent(projectId) + '/counts', { errorMap: COMMENT_ERRORS }),
  thread: (projectId: string, stage: CommentStage, before?: string, includeIds?: string[], threadId?: string): Promise<CommentThread> => {
    const query = new URLSearchParams();
    if (before) query.set('before', before);
    if (threadId) query.set('thread_id', threadId);
    if (includeIds?.length) query.set('include_ids', includeIds.slice(-100).join(','));
    return reqUrl(commentPath(projectId, stage) + (query.toString() ? '?' + query.toString() : ''), { errorMap: COMMENT_ERRORS });
  },
  anchors: (projectId: string, stage: CommentStage, before?: string): Promise<{ anchors: Array<Pick<StageComment, 'id' | 'stage' | 'anchor' | 'created_at' | 'author' | 'resolved_at'>>; next_cursor?: string | null }> =>
    reqUrl(commentPath(projectId, stage) + '/anchors' + (before ? '?before=' + encodeURIComponent(before) : ''), { errorMap: COMMENT_ERRORS }),
  post: (projectId: string, stage: CommentStage, body: string, mentions: string[], options: CommentPostOptions = {}): Promise<{ comment: StageComment }> =>
    reqUrl(commentPath(projectId, stage), { method: 'POST', json: { body, mentions, ...options }, errorMap: COMMENT_ERRORS }),
  edit: (id: string, body: string, mentions: string[]): Promise<{ comment: StageComment }> =>
    reqUrl(COMMENTS_BASE + '/' + encodeURIComponent(id), { method: 'PATCH', json: { body, mentions }, errorMap: COMMENT_ERRORS }),
  resolve: (id: string, resolved: boolean): Promise<{ comment: StageComment }> =>
    reqUrl(COMMENTS_BASE + '/' + encodeURIComponent(id) + '/resolve', { method: 'PATCH', json: { resolved }, errorMap: COMMENT_ERRORS }),
  remove: (id: string): Promise<{ ok: true }> =>
    reqUrl(COMMENTS_BASE + '/' + encodeURIComponent(id), { method: 'DELETE', errorMap: COMMENT_ERRORS }),
  upload: (projectId: string, stage: CommentStage, file: File, voice = false, duration?: number): Promise<{ attachment: CommentAttachment }> => {
    const body = new FormData(); body.append('file', file);
    if (voice) body.append('kind', 'voice');
    if (duration !== undefined) body.append('duration_ms', String(duration));
    return reqUrl(commentPath(projectId, stage) + '/attachments', { method: 'POST', body, errorMap: COMMENT_ERRORS });
  },
  discardAttachment: (id: string): Promise<{ ok: true }> =>
    reqUrl(COMMENTS_BASE + '/attachments/' + encodeURIComponent(id), { method: 'DELETE', errorMap: COMMENT_ERRORS }),
  attachmentBlob: async (id: string, signal?: AbortSignal): Promise<Blob> => {
    // Build the URL ourselves; never send credentials to an attachment-supplied origin.
    const response = await fetch(COMMENTS_BASE + '/attachments/' + encodeURIComponent(id), { signal });
    if (!response.ok) throw await mapError(response, COMMENT_ERRORS);
    return response.blob();
  },
  events: async (projectId: string, stage: CommentStage, signal: AbortSignal, receive: (event: string) => void): Promise<void> => {
    const response = await fetch(commentPath(projectId, stage) + '/events', { signal, headers: { Accept: 'text/event-stream' } });
    if (!response.ok) throw await mapError(response, COMMENT_ERRORS);
    if (!response.body || !response.headers.get('content-type')?.includes('text/event-stream')) throw new Error('Live updates unavailable');
    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    let pending = '';
    try {
      while (!signal.aborted) {
        const { done, value } = await reader.read();
        if (done) break;
        pending += decoder.decode(value, { stream: true }).replace(/\r/g, '');
        let boundary: number;
        while ((boundary = pending.indexOf('\n\n')) >= 0) {
          const block = pending.slice(0, boundary); pending = pending.slice(boundary + 2);
          const event = /^event:\s*(.+)$/m.exec(block)?.[1];
          if (event === 'ready' || event === 'comments.changed') receive(event);
        }
        if (pending.length > 65536) throw new Error('Invalid live update');
      }
    } finally { await reader.cancel().catch(() => {}); reader.releaseLock(); }
  },
};

/** The @mention composer logic, shared by the web panel and the mini app:
    the token being typed after an «@», and the people it matches. */
export function mentionQuery(text: string, caret: number): { start: number; query: string } | null {
  const before = text.slice(0, caret);
  const m = /(^|\s)@([\w.-]*)$/.exec(before);
  if (!m) return null;
  return { start: before.length - m[2].length - 1, query: m[2] };
}
export function filterMentionable(people: CommentPerson[], query: string, excludeId?: string | null): CommentPerson[] {
  const q = query.toLowerCase();
  return people
    .filter((p) => p.id !== excludeId)
    .filter((p) => !q || p.username.toLowerCase().includes(q) || (p.display_name || '').toLowerCase().includes(q))
    .slice(0, 8);
}
export function personLabel(p: CommentPerson): string {
  return p.display_name || p.username;
}
/** Render a body into plain/mention runs by matching «@username» of the
    comment's own mentions (never a regex over all users). */
export function splitMentions(body: string, mentions: CommentPerson[]): Array<{ text: string; person?: CommentPerson }> {
  if (!mentions.length) return [{ text: body }];
  const names = mentions.map((p) => p.username).sort((a, b) => b.length - a.length);
  const re = new RegExp('@(' + names.map((n) => n.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')).join('|') + ')(?![\\w.-])', 'g');
  const out: Array<{ text: string; person?: CommentPerson }> = [];
  let last = 0;
  let m: RegExpExecArray | null;
  while ((m = re.exec(body))) {
    if (m.index > last) out.push({ text: body.slice(last, m.index) });
    const username = m[1];
    const person = mentions.find((p) => p.username === username);
    out.push({ text: m[0], person });
    last = m.index + m[0].length;
  }
  if (last < body.length) out.push({ text: body.slice(last) });
  return out;
}

export const architectApi = {
  previewWorkflowRecovery: (projectId: string, input: WorkflowRecoveryInput): Promise<WorkflowRecoveryPreview> =>
    req('/projects/' + projectId + '/workflow-recovery/preview', { method: 'POST', json: input, errorMap: ARCHITECT_ERROR_EN }),

  restoreWorkflowRecovery: (projectId: string, input: WorkflowRecoveryInput & { expected_fingerprint: string; request_id: string }): Promise<WorkflowRecoveryResult> =>
    req('/projects/' + projectId + '/workflow-recovery/restore', { method: 'POST', json: input, errorMap: ARCHITECT_ERROR_EN }),

  listClients: (): Promise<{ clients: ArchitectClient[] }> => req('/clients'),

  createClient: (name: string, domain?: string): Promise<{ client: ArchitectClient }> =>
    req('/clients', {
      method: 'POST', json: { name, ...(domain !== undefined ? { domain } : {}) },
      errorMap: {
        client_exists: 'This client already exists. Select the existing client.',
        invalid_name: 'Enter a client name up to 200 characters.',
        invalid_domain: 'Enter a valid website or domain.',
      },
    }),

  renameClient: (clientId: string, name: string, expectedName: string): Promise<{ client: ArchitectClient }> =>
    req('/clients/' + encodeURIComponent(clientId), {
      method: 'PATCH', json: { name, expected_name: expectedName },
      errorMap: {
        client_exists: 'Another client already uses this name.',
        client_name_changed: 'The client name changed. Review the current name and save again.',
        client_not_found: 'This client is no longer available.',
        invalid_name: 'Enter a client name up to 200 characters.',
        invalid_expected_name: 'Reopen the editor to load the current client name.',
      },
    }),

  createClientPhase: (clientId: string, title: string): Promise<{ project: ArchitectProject }> =>
    req('/clients/' + encodeURIComponent(clientId) + '/phases', {
      method: 'POST', json: { title },
      errorMap: { invalid_phase_title: 'Enter a phase title up to 120 characters.', client_not_found: 'This client is no longer available.' },
    }),

  // A relationship update never invokes project rename or document generation.
  linkProjectClient: (projectId: string, link: ProjectClientLink): Promise<{ project: ArchitectProject }> =>
    req('/projects/' + encodeURIComponent(projectId) + '/client', {
      method: 'PUT', json: link,
      errorMap: {
        client_changed: 'This project’s client changed. Refresh and choose the client again.',
        client_not_found: 'This client is no longer available.',
        invalid_phase_title: 'Enter a phase title up to 120 characters.',
        invalid_expected_client_id: 'Refresh this project before changing its client.',
      },
    }),

  listProjects: (): Promise<{ projects: ProjectListItem[] }> => req('/projects'),

  createProject: (name: string): Promise<{ project: ProjectRecord }> =>
    req('/projects', { method: 'POST', json: { name } }),

  deleteProject: (id: string): Promise<any> =>
    req('/projects/' + id, { method: 'DELETE' }),

  // PATCH /projects/:id — the ONE project-metadata endpoint (DESIGN18 +
  // DESIGN20). Every field is optional and independent: `name` triggers the
  // server-side rename cascade (poster meta + every estimate/proposal title &
  // client field re-rendered IN PLACE; approvals/statuses/pricing/snapshots
  // untouched); `number` (integer 1000..99999, or null to clear) and
  // `doc_kind` are pure metadata — codes are computed, so they run NO cascade
  // and never touch approvals. Returns {project, pipeline} plus optional
  // `warnings` for any single document a rename could not re-render.
  patchProject: (
    id: string,
    patch: { name?: string; number?: number | null; doc_kind?: DocKind },
  ): Promise<RenameResult> =>
    req('/projects/' + id, {
      method: 'PATCH',
      json: patch,
      errorMap: RENAME_ERROR_EN,
    }),

  // Rename convenience wrapper (DESIGN18) — the name-only PATCH, kept as its
  // own call so existing callers (editor header, card, mini app) are unchanged.
  renameProject: (id: string, name: string): Promise<RenameResult> =>
    architectApi.patchProject(id, { name }),

  // GET /projects/:id returns the project row PLUS a TOP-LEVEL `pipeline`
  // sibling (DESIGN11 §P1 — statuses only). Unlike the list endpoint, the
  // pipeline is NOT nested on the project row, so the editor reads it from
  // this widened shape.
  getProject: (id: string): Promise<ProjectDetail & { pipeline?: Pipeline }> =>
    req('/projects/' + id),

  /** Idempotently continue an approved current revision, including projects
      approved before automatic hand-offs existed. The server alone chooses
      the next step; historical approvals never unlock a current stage. */
  resumePipeline: (id: string): Promise<{
    project: ArchitectProject;
    pipeline: Pipeline;
    auto_started: 'estimate' | 'pricing' | 'proposal' | null;
    resume_reason?: 'exists' | 'already_running' | 'no_approved_architecture' | 'no_approved_estimate' | 'pricing_not_approved' | 'disabled' | 'failed';
  }> => req('/projects/' + id + '/resume', { method: 'POST', errorMap: ARCHITECT_ERROR_EN }),

  // Approve the current architecture (DESIGN11 §P1) — canGenerate only on
  // the server. Returns the project with architecture_status='approved',
  // the approver name/time, and the refreshed pipeline snapshot as a
  // top-level sibling (same shape as GET /projects/:id).
  // ── Root's per-approver overview (DESIGN29, root only) ──
  //   403 for anyone else — the caller treats that as «not root, no detail».
  getApprovalsOverview: (): Promise<ApprovalsOverview> =>
    reqUrl(APPROVALS_BASE + '/overview', { errorMap: APPROVALS_ERROR_EN }),

  getPricingMethodology: (): Promise<{ methodology: PricingMethodology }> =>
    req('/pricing/methodology', { errorMap: ARCHITECT_ERROR_EN }),

  putPricingMethodology: (m: { day_rate: number; monthly_pct: number; note?: string | null; rules?: PricingRules }): Promise<{ methodology: PricingMethodology }> =>
    req('/pricing/methodology', { method: 'PUT', json: m, errorMap: ARCHITECT_ERROR_EN }),

  // Project pricing — explicit Pricing access; assignment and shared rates are Root only.
  pricingEstimateReference: (projectId: string): Promise<{ estimate: Pick<EstimateFull, 'id' | 'title' | 'status' | 'spec' | 'created_at' | 'reviewed_at' | 'reviewed_by_name'> | null }> =>
    req('/pricing/' + projectId + '/estimate-reference'),

  getPricing: (projectId: string): Promise<PricingSheetResponse> =>
    req('/pricing/' + projectId, { errorMap: ARCHITECT_ERROR_EN }).then(withSetupEmpty),

  getPricingHistory: (projectId: string): Promise<PricingHistoryResponse> =>
    req('/pricing/' + projectId + '/history', { errorMap: ARCHITECT_ERROR_EN }),

  recommendMonthlyPricing: (projectId: string, input: MonthlyRecommendationRequest): Promise<{ recommendation: MonthlyRecommendation }> =>
    req('/pricing/' + projectId + '/recommend-monthly', {
      method: 'POST', json: input, errorMap: ARCHITECT_ERROR_EN,
    }),

  getMonthlyPricingLibrary: (): Promise<MonthlyPricingLibrary> =>
    req('/pricing/monthly-library', { errorMap: ARCHITECT_ERROR_EN }),

  downloadMonthlyPricingBenchmark: async (id: string): Promise<Blob> => {
    const response: Response = await req('/pricing/monthly-library/' + encodeURIComponent(id) + '/download', {
      raw: true, errorMap: ARCHITECT_ERROR_EN,
    });
    return response.blob();
  },

  putPricing: (projectId: string, sheet: PricingSheet, guard?: { review_revision: number; estimate_id: string | null }): Promise<PricingSheetResponse> =>
    req('/pricing/' + projectId, {
      method: 'PUT',
      json: { sheet, ...guard },
      errorMap: ARCHITECT_ERROR_EN,
    }).then(withSetupEmpty),

  submitPricing: (projectId: string, input: { review_revision?: number } = {}): Promise<PricingSheetResponse> =>
    req('/pricing/' + projectId + '/submit', { method: 'POST', json: input, errorMap: ARCHITECT_ERROR_EN }).then(withSetupEmpty),

  requestPricingChanges: (projectId: string, input: { reason: string; review_revision?: number; override?: boolean }): Promise<PricingSheetResponse> =>
    req('/pricing/' + projectId + '/request-changes', { method: 'POST', json: input, errorMap: ARCHITECT_ERROR_EN }).then(withSetupEmpty),

  approvePricing: (projectId: string, input: { review_revision?: number; override?: boolean; approve_as_user_id?: string } = {}): Promise<PricingSheetResponse> =>
    req('/pricing/' + projectId + '/approve', {
      method: 'POST', json: input, errorMap: ARCHITECT_ERROR_EN,
    }).then(withSetupEmpty),

  assignPricing: (projectId: string, userId: string): Promise<PricingSheetResponse> =>
    req('/pricing/' + projectId + '/assignee', {
      method: 'PUT', json: { user_id: userId }, errorMap: ARCHITECT_ERROR_EN,
    }).then(withSetupEmpty),

  //
  // DESIGN23 §B1 — with a MULTI-approver policy the same call may record a
  // vote WITHOUT flipping the status: the response then carries the unchanged
  // project (architecture_status still 'draft') plus `approvals:{given,
  // required}`. With no policy (the default) nothing changes: one approval
  // flips it, exactly as today.

  // DESIGN27 — `auto_started` names the stage the server kicked off by itself
  // on this approval ('estimate' here). Optional on purpose: an older backend
  // omits it and the UI must simply say nothing rather than misreport.
  approveArchitecture: (
    id: string,
    input: { approve_as_user_id?: string; expected_architecture_version_id?: string | null } = {},
  ): Promise<{
    project: ArchitectProject;
    pipeline?: Pipeline;
    approvals?: ApprovalProgress;
    auto_started?: 'estimate' | 'pricing' | 'proposal' | null;
  }> =>
    req('/projects/' + id + '/architecture/approve', {
      method: 'POST',
      json: input,
      errorMap: { ...ARCHITECT_ERROR_EN, ...APPROVALS_ERROR_EN },
    }),

  uploadFiles: (id: string, fd: FormData): Promise<{ files?: FileMeta[] }> =>
    req('/projects/' + id + '/files', { method: 'POST', body: fd }),

  deleteFile: (id: string, fileId: string): Promise<any> =>
    req('/projects/' + id + '/files/' + fileId, { method: 'DELETE' }),

  // Persist an operator note on a source file ('' clears it).
  patchFileNote: (
    id: string,
    fileId: string,
    note: string,
  ): Promise<{ file: FileMeta }> =>
    req('/projects/' + id + '/files/' + fileId, {
      method: 'PATCH',
      json: { note },
    }),

  // Partial update of the structured generation inputs (main transcript,
  // extra instruction, requested phases). Returns the updated project meta.
  putInputs: (
    id: string,
    body: ProjectInputs,
  ): Promise<{ project: ProjectRecord }> =>
    req('/projects/' + id + '/inputs', { method: 'PUT', json: body }),

  // Start a generate run — 202 {run}; the caller polls getRun until done.
  generate: (id: string, body: { model: string } & Partial<ArchitectureEditRequest>): Promise<{ run: Run }> =>
    req('/projects/' + id + '/generate', { method: 'POST', json: body, errorMap: ARCHITECT_ERROR_EN }),

  // Start a prompt run — 202 {run}.
  promptRun: (
    id: string,
    body: { instruction: string; model: string } & Partial<ArchitectureEditRequest>,
  ): Promise<{ run: Run }> =>
    req('/projects/' + id + '/prompt', { method: 'POST', json: body, errorMap: ARCHITECT_ERROR_EN }),

  // Poll a run by id.
  getRun: (runId: string): Promise<{ run: Run }> => req('/runs/' + runId),

  // Stop a running generate/prompt run (DESIGN13 §F1). The POST is
  // idempotent server-side: a finished run comes back unchanged, a running
  // one flips to status 'error' with the operator-stop message. Returns the
  // updated run so the caller can clear its UI from the response.
  cancelRun: (runId: string): Promise<{ run: Run }> =>
    req('/runs/' + runId + '/cancel', { method: 'POST', json: {} }),

  // The project's active run (or the most recent within the resume
  // window) — used to restore progress after a page reload.
  getActiveRun: (id: string): Promise<{ run: Run | null }> =>
    req('/projects/' + id + '/runs/active'),

  putSpec: (id: string, spec: Spec, edit?: ArchitectureEditRequest): Promise<RenderResult> =>
    req('/projects/' + id + '/spec', { method: 'PUT', json: { spec, ...edit }, errorMap: ARCHITECT_ERROR_EN }),

  restore: (id: string, versionId: string, edit?: ArchitectureEditRequest): Promise<RenderResult> =>
    req('/projects/' + id + '/versions/' + versionId + '/restore', {
      method: 'POST',
      json: edit,
      errorMap: ARCHITECT_ERROR_EN,
    }),

  // SVG is returned as text and must be inlined into the DOM.
  getSvgText: (id: string): Promise<string> =>
    req('/projects/' + id + '/svg', { raw: true }).then((res: Response) =>
      res.text(),
    ),

  // The PDF is a blob download; the caller needs the raw Response for
  // the Content-Disposition filename.
  exportPdf: (id: string): Promise<Response> =>
    req('/projects/' + id + '/export.pdf', { raw: true }),
};

/* ═══════════ ESTIMATES ═══════════════════════════════════════
   The Development-Plan-&-Timelines estimator lives under
   /api/architect/estimate (mounted before /api/architect on the
   server). Its error codes are not in the shared ERROR_EN table, so
   these calls pass a local errorMap for the friendly English copy.

   DESIGN17 — ACCESS: the server guards the WHOLE router with the estimator
   rule, so EVERY call below returns 403 {error:'forbidden'} unless the
   viewer satisfies estimateAccess(...).canEstimate. Callers must gate the
   UI on that flag rather than firing a request and handling the failure —
   a generator has no path to any estimate document, by design. */

export type EstimateStatus = 'review' | 'approved' | 'rejected';

/* ── estimate generation runs (mirror the poster RUN pattern) ──
   POST /generate is async: it returns a tracked run (202) and the UI
   polls GET /runs/:id until done/error, so a long LLM call survives
   proxy timeouts and — via /runs/active on open — page reloads. The
   shape mirrors the backend estimate_runs row 1:1 (snake_case wire). */

export type EstimateRunStatus = 'running' | 'done' | 'error';
/** Live sub-step of an estimate run; drives the stepper. */
export type EstimateRunStage =
  | 'preparing'
  | 'llm'
  | 'llm-retry'
  | 'validating'
  | 'rendering'
  | 'saving';

export interface EstimateRun {
  id: string;
  project_id: string;
  /** Set at the 'saving' stage once the estimate_docs row exists. */
  estimate_id: string | null;
  status: EstimateRunStatus;
  stage: string; // one of EstimateRunStage, but tolerate unknown values
  model: string | null;
  error: string | null;
  telemetry: unknown | null;
  created_at: string;
  updated_at: string;
  finished_at: string | null;
}

export interface EstimateMeta {
  id: string;
  status: EstimateStatus;
  model: string | null;
  title: string;
  created_at: string;
  created_by_name?: string | null;
  reviewed_by_name?: string | null;
  reviewed_at?: string | null;
  review_note?: string | null;
  has_docx?: boolean;
}

/** GET /estimate/projects/:id — the estimate list. DESIGN17: only an
 *  estimator (or root / non-confined dev) can reach it at all; every other
 *  caller gets 403, so the list is always the FULL set of rows for the
 *  callers who see it. */
export interface EstimateList {
  estimates: EstimateMeta[];
  /** @deprecated DESIGN17 — the generator-only «N under review» counter. No
   *  generator can call this endpoint any more, so the server may stop
   *  sending it; kept optional so an older response still typechecks. */
  pending_review?: number;
}

/* The editable estimate document. The server is the authority on
   validation and on every computed field (per-case subtotal, root total,
   total_label, title) — the editor mirrors the recompute rules only for a
   live preview and sends the spec back WITHOUT any computed field.

   Schema v2 (the shape the editor speaks): the GET/:id endpoint returns
   every stored estimate already wrapped to v2, so the editor never has to
   read v1 — but a defensive local wrap is kept in the editor anyway. The
   v1 fields below stay declared for that wrap's input side. */

/** A module's estimate: a single value, an inclusive [min,max] range, or
    one of the two "undefined" markers ('TBD' — needs scoping; '–' — not
    applicable / out of scope). Ranges and undefined markers require a
    reason on the server for TBD/–. */
export type EstimateDays = number | [number, number] | 'TBD' | '–';

export interface EstimateModule {
  name: string;
  description: string;
  apis: string[];
  days: EstimateDays;
  tbd_reason?: string;
}

export interface EstimateCase {
  name: string;
  modules: EstimateModule[];
  /** Server-computed; ignored on the way up, recomputed live for preview. */
  subtotal?: { min: number; max: number };
}

export interface EstimateSummary {
  deliverables: string;
  team: string;
}

export interface EstimateSpec {
  version: number;
  client: string;
  system: string;

  /* ── v2 ── */
  cases?: EstimateCase[];
  notes?: string[];
  summary?: EstimateSummary;
  /** Server-computed root fields (never sent up by the editor). */
  total?: { min: number; max: number; excluded: number };
  total_label?: string;
  title?: string;

  /* ── v1 (legacy input to the defensive local wrap) ── */
  modules?: EstimateModule[];
  total_days?: number;
  total_weeks?: string;
  important?: string[];
  tbd?: string[];
}

/** Meta row plus the full editable spec (returned by GET /:id and the
    PUT /:id/spec save). */
export interface EstimateFull extends EstimateMeta {
  spec: EstimateSpec;
}

/** Token/cost telemetry the AI-chat prompt endpoint returns alongside the
    updated estimate. Every field is optional — the footnote renders what
    it has. Mirrors the backend EstimateTelemetry shape (camelCase wire). */
export interface EstimateTelemetry {
  model?: string;
  inputTokens?: number;
  cacheReadTokens?: number;
  cacheCreationTokens?: number;
  outputTokens?: number;
  costUsd?: number;
  durationMs?: number;
}

/* ═══════════ ESTIMATE CHAT (DESIGN22 §C) ═════════════════════
   The AI panel beside the estimate document has TWO modes and its thread
   now lives on the SERVER (it used to be sessionStorage, so it vanished on
   reload — the owner's complaint «я тут сколько-то написал, а потом всё»):
     • edit — POST /:id/prompt, the existing document-editing turn;
     • ask  — POST /:id/ask, a question ABOUT the estimate that returns
              prose and leaves the document byte-identical.
   Both turns are persisted by the backend and replayed by GET /:id/chat
   (newest-last, capped server-side); DELETE /:id/chat clears the thread. */

export type EstimateChatRole = 'user' | 'assistant';
export type EstimateChatMode = 'edit' | 'ask';

/** One persisted chat turn (wire shape = the stored row, snake_case). The
    assistant line of an EDIT turn is a neutral server-side summary — the
    live diff chips are computed client-side from the returned spec and are
    intentionally not stored, so a replayed edit turn renders as prose. */
export interface EstimateChatMessage {
  id: string;
  role: EstimateChatRole;
  mode: EstimateChatMode;
  content: string;
  telemetry?: EstimateTelemetry | null;
  created_at?: string;
}

/* Estimate-surface error codes. Exported (DESIGN15 §F2.2) as the single
   source the mini app also consumes — the same code→English text on both
   surfaces, no fork. Includes the gate codes (no_approved_architecture,
   no_source_content) and the per-doc lock (estimate_busy). */
export const ESTIMATE_ERROR_EN: Record<string, string> = {
  approval_delegate_root_only: 'Only Root can approve for another reviewer.',
  invalid_approval_delegate: 'Choose an active reviewer for this stage.',
  estimate_already_running:
    'An estimate for this project is already running',
  // DESIGN12 §F3 — the estimate-generation gate: the architecture must be
  // approved before an estimate can be sent (enforced for every caller,
  // root included). The UI also disables the button + shows an inline hint,
  // so this is a backstop message if a request slips through.
  no_approved_architecture: 'Approve the architecture first',
  openai_not_configured: 'The Max model is not configured on the server',
  no_source_content:
    'No source text: add notes or files to the project first',
  estimate_failed: 'The estimator could not complete — try again',
  invalid_spec: 'The document failed validation',
  render_failed: 'The document could not be rendered — try again',
  already_decided: 'This estimate has already been reviewed',
  no_docx: 'No document is available for this estimate',
  not_found: 'Estimate not found',
  forbidden: 'Your account cannot perform that action',
  estimate_busy: 'This estimate is being edited — try again in a moment',
  // DESIGN22 §C — Ask mode (question-only turns; the document is untouched).
  invalid_question: 'Type a question first',
  ask_failed: 'The assistant could not answer — try again',
  // DESIGN23 §C1 — a configured approver list can refuse this caller.
  not_an_approver: 'You are not an approver for this step',
};

export const estimateApi = {
  listEstimates: (projectId: string): Promise<EstimateList> =>
    req('/estimate/projects/' + projectId, { errorMap: ESTIMATE_ERROR_EN }),

  // Start an estimate run — 202 {run}; the caller polls getEstimateRun
  // until it is done/error (mirrors architectApi.generate).
  generateEstimate: (
    projectId: string,
    model: string,
  ): Promise<{ run: EstimateRun }> =>
    req('/estimate/projects/' + projectId + '/generate', {
      method: 'POST',
      json: { model },
      errorMap: ESTIMATE_ERROR_EN,
    }),

  // Poll an estimate run by id.
  getEstimateRun: (runId: string): Promise<{ run: EstimateRun }> =>
    req('/estimate/runs/' + runId, { errorMap: ESTIMATE_ERROR_EN }),

  // Stop a running estimate run (DESIGN13 §F1) — idempotent server-side
  // (mirrors architectApi.cancelRun). Returns the updated run.
  cancelEstimateRun: (runId: string): Promise<{ run: EstimateRun }> =>
    req('/estimate/runs/' + runId + '/cancel', {
      method: 'POST',
      json: {},
      errorMap: ESTIMATE_ERROR_EN,
    }),

  // The project's active estimate run (or the most recent within the
  // resume window) — used to restore the stepper after a page reload.
  getActiveEstimateRun: (
    projectId: string,
  ): Promise<{ run: EstimateRun | null }> =>
    req('/estimate/projects/' + projectId + '/runs/active', {
      errorMap: ESTIMATE_ERROR_EN,
    }),

  // Raw Response so the caller can read the Content-Disposition filename.
  estimateDocxBlob: (id: string): Promise<Response> =>
    req('/estimate/' + id + '/docx', { raw: true, errorMap: ESTIMATE_ERROR_EN }),

  // Full estimate incl. the editable spec (for the document editor).
  getEstimate: (id: string): Promise<{ estimate: EstimateFull }> =>
    req('/estimate/' + id, { errorMap: ESTIMATE_ERROR_EN }),

  // Re-validate + re-render + store the edited spec. On success the server
  // returns the fresh meta (status back to 'review') plus the stored spec
  // with server-authoritative totals.
  saveEstimateSpec: (
    id: string,
    spec: EstimateSpec,
  ): Promise<{ estimate: EstimateFull }> =>
    req('/estimate/' + id + '/spec', {
      method: 'PUT',
      json: { spec },
      errorMap: ESTIMATE_ERROR_EN,
    }),

  // AI chat edit: apply a natural-language instruction to the estimate.
  // The server re-validates, recomputes and re-renders exactly like a
  // manual save (status → review), and returns the fresh full estimate
  // plus token/cost telemetry. `estimate_busy` (409) is the per-estimate
  // lock backstop — the chat client also serialises its own sends.
  promptEstimate: (
    id: string,
    body: { instruction: string; model: string },
  ): Promise<{ estimate: EstimateFull; telemetry?: EstimateTelemetry }> =>
    req('/estimate/' + id + '/prompt', {
      method: 'POST',
      json: body,
      errorMap: {
        ...ESTIMATE_ERROR_EN,
        estimate_busy:
          'The AI is already editing this document — wait a moment',
        invalid_instruction: 'Type what you want changed first',
      },
    }),

  // AI ask (DESIGN22 §C): a question ABOUT the estimate. The server answers
  // in prose with the same estimate methodology in context and NEVER touches
  // the document — no spec comes back, so the editor keeps its state exactly
  // as it is. Shares the per-estimate lock with /prompt (409 estimate_busy)
  // so an answer and an edit can't interleave on one document.
  askEstimate: (
    id: string,
    question: string,
    model: string,
  ): Promise<{ answer: string; telemetry?: EstimateTelemetry }> =>
    req('/estimate/' + id + '/ask', {
      method: 'POST',
      json: { question, model },
      errorMap: {
        ...ESTIMATE_ERROR_EN,
        estimate_busy: 'The AI is already working on this document — wait a moment',
      },
    }),

  // The persisted chat thread for one estimate (newest-last, server-capped).
  // Loaded on mount so the conversation survives a reload or a new tab.
  getEstimateChat: (
    id: string,
  ): Promise<{ messages: EstimateChatMessage[] }> =>
    req('/estimate/' + id + '/chat', { errorMap: ESTIMATE_ERROR_EN }),

  // Clear the whole thread for one estimate («Clear history» in the panel).
  clearEstimateChat: (id: string): Promise<unknown> =>
    req('/estimate/' + id + '/chat', {
      method: 'DELETE',
      errorMap: ESTIMATE_ERROR_EN,
    }),

  // Approve is the ONLY estimate decision — Reject was removed product-wide
  // (DESIGN10) and its backend route deleted.
  //
  // DESIGN23 §B2 — with a multi-approver policy this records a vote and the
  // estimate STAYS in 'review' until the threshold is met; `approvals` carries
  // the «1 of 2». With no policy it flips on the first approval, as today.
  // Final approval prepares the root-owned pricing draft; the proposal waits
  // for an explicit Pricing approval. The handoff field is optional.
  approveEstimate: (
    id: string,
    feedback?: string,
    input: { approve_as_user_id?: string } = {},
  ): Promise<{
    estimate: EstimateMeta;
    knowledge?: unknown;
    approvals?: ApprovalProgress;
    auto_started?: 'estimate' | 'pricing' | 'proposal' | null;
  }> =>
    req('/estimate/' + id + '/approve', {
      method: 'POST',
      json: { ...(feedback ? { feedback } : {}), ...input },
      errorMap: ESTIMATE_ERROR_EN,
    }),
};


/* ═══════════════════════════════════════════════════════════
   Signed downloads (DESIGN47)
   ═══════════════════════════════════════════════════════════
   A short-lived link that works WITHOUT the Bearer — for Telegram's own
   downloader, the system browser, or a plain new tab. The server re-applies
   the file's access rule when minting and again when serving. */

export type DownloadKind = 'poster_pdf' | 'estimate_docx' | 'proposal_docx' | 'monthly_benchmark_docx';

export interface SignedDownload {
  token: string;
  /** Server-relative: prefix with the API origin (downloadUrl). */
  path: string;
  filename: string;
  expires_in: number;
}

export const downloadsApi = {
  sign: (kind: DownloadKind, id: string, inline = false): Promise<SignedDownload> =>
    reqUrl(API + '/api/architect/downloads/sign', {
      method: 'POST',
      json: { kind, id, inline },
      errorMap: { not_found: 'The document is not available yet', forbidden: 'You have no access to this document' },
    }),
};

/** The absolute URL of a signed download. */
export function downloadUrl(d: SignedDownload): string {
  return API + d.path;
}
